import { useParams, Link } from "wouter";
import {
  useGetMerchant,
  getGetMerchantQueryKey,
  useListListings,
  getListListingsQueryKey,
} from "@workspace/api-client-react";
import { useI18n } from "@/contexts/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ListingImage } from "@/components/listing-image";
import { buildMerchantWhatsAppMessage, buildWhatsAppUrl } from "@/lib/whatsapp";
import { trackWhatsAppClicked, trackPhoneClicked, trackWebsiteClicked } from "@/lib/analytics";
import {
  MapPin,
  Store,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Globe,
  Instagram,
  Phone,
  Mail,
  MessageCircle,
  Clock,
  ArrowRight
} from "lucide-react";
import React, { useEffect } from "react";

function serviceMethodLabel(method: string) {
  return ({
    merchant_location: "Service at Merchant Location",
    customer_location: "Service at Customer Location / Home Service",
    physical_goods: "Physical Goods",
    delivery: "Delivery Available",
    pickup: "Pickup Available",
    online_remote: "Online / Remote Service",
  } as Record<string, string>)[method] ?? method;
}

export default function MerchantDetailPage() {
  const params = useParams();
  const slugOrId = params.slug as string;
  const { t, isRTL } = useI18n();

  const { data: merchant, isLoading, error } = useGetMerchant(slugOrId, {
    query: { enabled: !!slugOrId, queryKey: getGetMerchantQueryKey(slugOrId) },
  });

  const { data: listingsPage, isLoading: listingsLoading } = useListListings(
    { merchantId: merchant?.id, limit: 100 },
    {
      query: {
        enabled: !!merchant?.id,
        queryKey: getListListingsQueryKey({ merchantId: merchant?.id, limit: 100 }),
      },
    },
  );

  const listings = listingsPage?.listings ?? [];

  // Canonical <link> for SEO
  useEffect(() => {
    if (!merchant) return;
    const canonicalSlug = merchant.slug ?? slugOrId;
    const href = `${window.location.origin}/merchants/${canonicalSlug}`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = href;
    return () => {
      link?.remove();
    };
  }, [merchant, slugOrId]);

  // Open Graph + Twitter meta
  useEffect(() => {
    if (!merchant) return;

    const canonicalSlug = merchant.slug ?? slugOrId;
    const pageUrl = `${window.location.origin}/merchants/${canonicalSlug}`;
    const image = merchant.logoUrl ?? merchant.imageUrl ?? "";

    const ogTitle = `${merchant.name} | Souq Crypto`;
    const ogDescription =
      merchant.description?.slice(0, 160) ||
      `Browse all USDT listings from ${merchant.name} on Souq Crypto.`;

    const upsertMeta = (
      attrKey: "name" | "property",
      attrValue: string,
      content: string,
    ): HTMLMetaElement => {
      let el = document.querySelector<HTMLMetaElement>(
        `meta[${attrKey}="${attrValue}"]`,
      );
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrKey, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const prevTitle = document.title;
    document.title = ogTitle;

    upsertMeta("property", "og:title", ogTitle);
    upsertMeta("property", "og:description", ogDescription);
    upsertMeta("property", "og:url", pageUrl);
    upsertMeta("property", "og:type", "website");
    if (image) upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", ogTitle);
    upsertMeta("name", "twitter:description", ogDescription);
    if (image) upsertMeta("name", "twitter:image", image);

    return () => {
      document.title = prevTitle;
      upsertMeta("property", "og:title", "Souq Crypto — Discover Where to Spend USDT in Dubai");
      upsertMeta("property", "og:description", "Find Dubai merchants, products, and services that accept USDT directly.");
      upsertMeta("property", "og:url", window.location.origin);
      upsertMeta("property", "og:type", "website");
      upsertMeta("property", "og:image", "/og-default.png");
      upsertMeta("name", "twitter:card", "summary_large_image");
      upsertMeta("name", "twitter:title", "Souq Crypto — Discover Where to Spend USDT in Dubai");
      upsertMeta("name", "twitter:description", "Find Dubai merchants, products, and services that accept USDT directly.");
      upsertMeta("name", "twitter:image", "/og-default.png");
    };
  }, [merchant, slugOrId]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !merchant) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-muted/10">
        <AlertCircle className="h-16 w-16 text-destructive mb-6" />
        <h2 className="text-3xl font-extrabold mb-3 text-[#062A60] dark:text-white">{t("Merchant not found")}</h2>
        <p className="text-lg text-muted-foreground mb-8 max-w-md">
          {t("The merchant you are looking for does not exist or has been removed.")}
        </p>
        <Button size="lg" className="rounded-full px-8 font-bold shadow-md" asChild>
          <Link href="/explore">{t("Back to Explore")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-muted/10 pb-20">
      <div className="container mx-auto px-4 pt-8 pb-16 max-w-6xl">
        {/* Back link */}
        <Link
          href="/explore"
          className="inline-flex items-center text-sm font-bold text-muted-foreground hover:text-primary mb-8 transition-colors"
          data-testid="link-back-to-explore"
        >
          <ArrowLeft className={`mr-2 h-4 w-4 ${isRTL ? "rtl-flip" : ""}`} /> {t("Back to listings")}
        </Link>

        {/* Merchant Hero */}
        <div className="rounded-3xl overflow-hidden border border-border/60 bg-card mb-12 shadow-sm">
          {/* Cover image */}
          {merchant.imageUrl && (
            <div className="aspect-[3/1] w-full overflow-hidden bg-muted">
              <ListingImage
                src={merchant.imageUrl}
                alt={merchant.name}
                imgClassName="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-10">
            <div className="flex flex-col sm:flex-row gap-8 items-start">
              {/* Logo / Avatar */}
              <div className="h-28 w-28 rounded-3xl bg-background border-4 border-card flex items-center justify-center text-4xl font-extrabold text-primary shadow-lg shrink-0 overflow-hidden -mt-16 sm:-mt-20 ring-1 ring-border/20 z-10 relative">
                {merchant.logoUrl ? (
                  <img
                    src={merchant.logoUrl}
                    alt={merchant.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  merchant.name.charAt(0)
                )}
              </div>

              <div className="flex-1 min-w-0">
                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {merchant.isVerified && (
                    <Badge className="bg-primary hover:bg-primary text-primary-foreground font-bold px-4 py-1.5 text-sm rounded-full gap-1.5 shadow-sm">
                      <CheckCircle2 className="h-4 w-4" />
                      {t("Verified")}
                    </Badge>
                  )}
                  {merchant.isFeatured && (
                    <Badge
                      variant="outline"
                      className="border-accent text-accent font-bold px-4 py-1.5 text-sm rounded-full bg-accent/5 shadow-sm"
                    >
                      {t("Featured")}
                    </Badge>
                  )}
                  <Badge variant="secondary" className="souq-category-badge px-4 py-1.5 text-sm rounded-full font-bold shadow-sm">
                    {merchant.categoryName}
                  </Badge>
                </div>

                <h1
                  className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-[#062A60] dark:text-white"
                  data-testid="text-merchant-name"
                >
                  {merchant.name}
                </h1>

                <div className="flex flex-wrap items-center gap-6 text-muted-foreground text-sm font-semibold mb-6">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span>{merchant.area}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    <span>
                      {merchant.listingCount ?? 0}{" "}
                      {(merchant.listingCount ?? 0) === 1 ? t("listing") : t("listings")}
                    </span>
                  </div>
                </div>

                {merchant.description && (
                  <p className="text-muted-foreground leading-relaxed text-base md:text-lg max-w-3xl">
                    {merchant.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* Listings grid */}
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-extrabold mb-8 text-[#062A60] dark:text-white" data-testid="text-listings-heading">
              {t("All Listings")}
              {listings.length > 0 && (
                <span className="ml-3 text-xl font-bold text-muted-foreground">
                  ({listings.length})
                </span>
              )}
            </h2>

            {listingsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : listings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center text-muted-foreground bg-card rounded-3xl border border-border/60">
                <Store className="h-12 w-12 mb-4 opacity-40" />
                <p className="text-lg font-medium">{t("No listings available for this merchant yet.")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {listings.map((listing) => (
                  <Link
                    key={listing.id}
                    href={`/listings/${listing.slug ?? listing.id}`}
                    className="block h-full group"
                    data-testid={`card-listing-${listing.id}`}
                  >
                    <Card className="overflow-hidden rounded-3xl border-border/60 hover:border-primary/50 hover:shadow-xl transition-all duration-300 flex flex-col h-full hover:-translate-y-1">
                      <div className="relative overflow-hidden bg-muted aspect-[4/3] w-full">
                        <ListingImage
                          src={listing.imageUrl}
                          alt={listing.title}
                          imgClassName="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                          <Badge className="souq-usdt-badge bg-primary/95 hover:bg-primary text-primary-foreground backdrop-blur-sm shadow-sm font-bold px-3 py-1 text-xs">
                            {t("USDT Accepted")}
                          </Badge>
                          {listing.isFeatured && (
                            <Badge
                              variant="outline"
                              className="bg-accent/90 border-accent-foreground/10 text-accent-foreground backdrop-blur-sm shadow-sm font-bold px-3 py-1 text-xs"
                            >
                              {t("Premium Only")}
                            </Badge>
                          )}
                        </div>
                        <div className="absolute bottom-3 right-3">
                          <Badge
                            variant="secondary"
                            className="souq-category-badge backdrop-blur-md font-bold shadow-md px-3 py-1.5 text-xs"
                          >
                            {listing.categoryName}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex-1 flex flex-col p-6">
                        <div className="mb-2">
                          <h3 className="font-bold text-xl line-clamp-2 group-hover:text-primary transition-colors leading-tight">
                            {listing.title}
                          </h3>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4 font-semibold">
                          <MapPin className="h-3.5 w-3.5" />
                          <span className="truncate">{listing.area}</span>
                        </div>
                        
                        <p className="text-muted-foreground text-sm line-clamp-2 mb-6 flex-1 leading-relaxed">
                          {listing.description}
                        </p>
                        
                        <div className="pt-5 mt-auto border-t border-border/50 flex items-center justify-between">
                          <div className="font-extrabold text-lg text-[#062A60] dark:text-white">
                            {listing.priceAed
                              ? `${listing.priceAed.toLocaleString()} ${t("AED")}`
                              : t("Contact for price")}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="font-bold rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-all"
                          >
                            {t("View Details")} <ArrowRight className={`h-4 w-4 ${isRTL ? "rtl-flip mr-1" : "ml-1"}`} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar — merchant info */}
          <div className="space-y-6">
            {/* Contact card */}
            {(merchant.phone || merchant.whatsapp || merchant.email || merchant.website || merchant.instagram) && (
              <Card className="border-border/60 rounded-3xl overflow-hidden shadow-sm">
                <CardContent className="p-6 md:p-8">
                  <h3 className="font-bold text-sm uppercase tracking-widest text-muted-foreground mb-6">
                    {t("Contact")}
                  </h3>
                  <div className="space-y-4">
                    {merchant.whatsapp && (
                      <a
                        href={buildWhatsAppUrl(
                          merchant.whatsapp,
                          buildMerchantWhatsAppMessage({
                            merchantName: merchant.name,
                            merchantUrl: window.location.href,
                          }),
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 text-base font-medium text-foreground hover:text-primary transition-colors"
                        onClick={() => trackWhatsAppClicked({ merchant_id: merchant.id, category: merchant.categoryName })}
                        data-testid="link-whatsapp"
                      >
                        <div className="h-10 w-10 rounded-full bg-[#25D366]/10 flex items-center justify-center shrink-0">
                          <MessageCircle className="h-5 w-5 text-[#25D366]" />
                        </div>
                        <span>{merchant.whatsapp}</span>
                      </a>
                    )}
                    {merchant.phone && (
                      <a
                        href={`tel:${merchant.phone}`}
                        className="flex items-center gap-4 text-base font-medium text-foreground hover:text-primary transition-colors"
                        onClick={() => trackPhoneClicked({ merchant_id: merchant.id, category: merchant.categoryName })}
                        data-testid="link-phone"
                      >
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Phone className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <span>{merchant.phone}</span>
                      </a>
                    )}
                    {merchant.email && (
                      <a
                        href={`mailto:${merchant.email}`}
                        className="flex items-center gap-4 text-base font-medium text-foreground hover:text-primary transition-colors"
                        data-testid="link-email"
                      >
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Mail className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <span className="truncate">{merchant.email}</span>
                      </a>
                    )}
                    {merchant.website && (
                      <a
                        href={merchant.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 text-base font-medium text-foreground hover:text-primary transition-colors"
                        onClick={() => trackWebsiteClicked({ merchant_id: merchant.id, category: merchant.categoryName })}
                        data-testid="link-website"
                      >
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Globe className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <span className="truncate">{merchant.website.replace(/^https?:\/\//, "")}</span>
                      </a>
                    )}
                    {merchant.instagram && (
                      <a
                        href={`https://instagram.com/${merchant.instagram.replace(/^@/, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 text-base font-medium text-foreground hover:text-primary transition-colors"
                        data-testid="link-instagram"
                      >
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Instagram className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <span>@{merchant.instagram.replace(/^@/, "")}</span>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Opening hours */}
            {merchant.openingHours && (
              <Card className="border-border/60 rounded-3xl overflow-hidden shadow-sm">
                <CardContent className="p-6 md:p-8">
                  <h3 className="font-bold text-sm uppercase tracking-widest text-muted-foreground mb-4">
                    {t("Opening Hours")}
                  </h3>
                  <div className="flex items-start gap-4 text-base font-medium text-foreground">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <span className="whitespace-pre-wrap pt-2 leading-relaxed">{merchant.openingHours}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Service methods */}
            {merchant.serviceMethods?.length > 0 && (
              <Card className="border-border/60 rounded-3xl overflow-hidden shadow-sm">
                <CardContent className="p-6 md:p-8">
                  <h3 className="font-bold text-sm uppercase tracking-widest text-muted-foreground mb-5">
                    {t("Service Methods")}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {merchant.serviceMethods.map((method) => (
                      <Badge key={method} variant="secondary" className="bg-muted px-4 py-2 font-medium">
                        {t(serviceMethodLabel(method) as Parameters<typeof t>[0])}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Supported networks */}
            {(merchant.supportedNetworks?.length ?? 0) > 0 && (
              <Card className="border-border/60 rounded-3xl overflow-hidden shadow-sm border-primary/20">
                <CardContent className="p-6 md:p-8">
                  <h3 className="font-bold text-sm uppercase tracking-widest text-primary mb-5">
                    {t("USDT Networks")}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {merchant.supportedNetworks?.map((net) => (
                      <Badge key={net} className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20 px-4 py-2 font-bold text-sm transition-colors">
                        {net}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
